import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Camera, Calendar, Clock, Cloud, Send, Image as ImageIcon, X, Plus, AlertTriangle, Lock, Eye, PenTool, CheckCircle2 } from "lucide-react";

/**
 * <DailyLogTimeline projectId /> — read-only timeline of daily logs.
 * <DailyLogComposer  projectId onSaved /> — employee/admin write form.
 *
 * Pattern: Buildertrend + CompanyCam mashup, but inline and brand-styled.
 */

function fmtDate(d) {
  if (!d) return "";
  return new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export function DailyLogTimeline({ projectId, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/projects/${projectId}/daily-logs`, { params: { limit: 50 } })
      .then((r) => { if (!cancelled) setItems(r.data); })
      .catch((err) => { console.warn("daily logs fetch:", err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, refreshKey]);

  if (loading) {
    return <div className="text-sm text-[#475569] py-6 text-center" data-testid="daily-log-loading">Loading field log…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="text-sm text-[#475569] text-center py-10 border border-dashed border-[#E2E8F0] dark:border-slate-700 rounded-lg" data-testid="daily-log-empty">
        No daily logs yet. The crew posts here as work happens.
      </div>
    );
  }

  return (
    <ol className="space-y-3" data-testid="daily-log-timeline">
      {items.map((log) => (
        <li
          key={log.log_id}
          data-testid={`daily-log-${log.log_id}`}
          className="card-pb p-4 border-l-4 border-l-[#C9A356]"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFFBF0] text-[#8A6B14] border border-[#E8CF7C]">
                <Calendar className="w-3 h-3" /> {fmtDate(log.date)}
              </span>
              <span className="text-[#475569] dark:text-slate-400">by <strong>{log.employee_name}</strong></span>
            </div>
            <div className="flex items-center gap-3 text-xs text-[#475569] dark:text-slate-400">
              {log.hours != null && (
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {log.hours}h</span>
              )}
              {log.weather && (
                <span className="inline-flex items-center gap-1"><Cloud className="w-3 h-3" /> {log.weather}</span>
              )}
            </div>
          </div>
          <p className="text-sm text-[#0A2540] dark:text-white whitespace-pre-line">{log.summary}</p>
          {log.photos?.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {log.photos.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <img src={url} alt="" loading="lazy"
                       className="h-24 w-32 object-cover rounded-md border border-[#E2E8F0] dark:border-slate-700 hover:scale-[1.02] transition" />
                </a>
              ))}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

export function DailyLogComposer({ projectId, onSaved }) {
  const [summary, setSummary] = useState("");
  const [hours, setHours] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [weather, setWeather] = useState("");
  const [hurricaneNotes, setHurricaneNotes] = useState("");
  const [siteConditions, setSiteConditions] = useState("");
  const [tasksInput, setTasksInput] = useState("");
  const [tasks, setTasks] = useState([]);
  const [used, setUsed] = useState([]);
  const [returned, setReturned] = useState([]);
  const [damaged, setDamaged] = useState([]);
  const [issues, setIssues] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [followUp, setFollowUp] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureData, setSignatureData] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Auto-compute hours from start/end times.
  useEffect(() => {
    if (startTime && endTime) {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      if (!Number.isNaN(sh) && !Number.isNaN(eh)) {
        let diff = (eh + em / 60) - (sh + sm / 60);
        if (diff < 0) diff += 24;
        if (diff > 0 && diff <= 24) setHours(diff.toFixed(2));
      }
    }
  }, [startTime, endTime]);

  const addTask = () => {
    const t = tasksInput.trim();
    if (!t) return;
    setTasks((prev) => [...prev, t].slice(0, 30));
    setTasksInput("");
  };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", projectId);
      fd.append("category", "photo");
      const r = await api.post("/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data?.url) setPhotos((p) => [...p, r.data.url]);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    Array.from(e.target.files || []).slice(0, 8 - photos.length).forEach(upload);
    e.target.value = "";
  };

  const submit = async () => {
    const trimmed = summary.trim();
    if (!trimmed) {
      toast.error("Add a quick summary first");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/daily-logs`, {
        summary: trimmed,
        hours: hours ? Number(hours) : undefined,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        weather: weather || undefined,
        hurricane_notes: hurricaneNotes || undefined,
        site_conditions: siteConditions || undefined,
        tasks_completed: tasks,
        materials_used: used,
        materials_returned: returned,
        materials_damaged: damaged,
        issues: issues || undefined,
        customer_notes: customerNotes || undefined,
        internal_notes: internalNotes || undefined,
        follow_up_required: followUp,
        follow_up_notes: followUpNotes || undefined,
        customer_signature: signatureData || undefined,
        photos,
      });
      toast.success("Field log posted");
      setSummary(""); setHours(""); setStartTime(""); setEndTime("");
      setWeather(""); setHurricaneNotes(""); setSiteConditions("");
      setTasks([]); setUsed([]); setReturned([]); setDamaged([]);
      setIssues(""); setCustomerNotes(""); setInternalNotes("");
      setFollowUp(false); setFollowUpNotes(""); setSignatureData(""); setPhotos([]);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card-pb p-5" data-testid="daily-log-composer">
      <div className="flex items-center gap-2 mb-3">
        <Camera className="w-4 h-4 text-[#C9A356]" />
        <span className="overline">Post today's field report</span>
      </div>

      {/* 1. Summary — always required */}
      <textarea
        rows="2"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        placeholder="One-line summary of the day…"
        data-testid="daily-log-summary"
        maxLength={2000}
        className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356]"
      />

      {/* 2. Time / Hours / Weather */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <FieldWrap label="Start">
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                 data-testid="daily-log-start" className={INPUT_CLS} />
        </FieldWrap>
        <FieldWrap label="End">
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                 data-testid="daily-log-end" className={INPUT_CLS} />
        </FieldWrap>
        <FieldWrap label="Hours">
          <input type="number" step="0.5" min="0" max="24" value={hours}
                 onChange={(e) => setHours(e.target.value)} placeholder="e.g. 6.5"
                 data-testid="daily-log-hours" className={INPUT_CLS} />
        </FieldWrap>
        <FieldWrap label="Weather">
          <input type="text" value={weather} onChange={(e) => setWeather(e.target.value)}
                 placeholder="Sunny, 28°C, light wind" maxLength={80}
                 data-testid="daily-log-weather" className={INPUT_CLS} />
        </FieldWrap>
      </div>

      {/* 3. Hurricane / site conditions */}
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <FieldWrap label="Hurricane / storm notes" hint="Watches, prep steps, safety measures">
          <input type="text" value={hurricaneNotes} onChange={(e) => setHurricaneNotes(e.target.value)}
                 placeholder="e.g. TS Watch — panels racked, batteries covered"
                 maxLength={500} data-testid="daily-log-hurricane" className={INPUT_CLS} />
        </FieldWrap>
        <FieldWrap label="Site conditions">
          <input type="text" value={siteConditions} onChange={(e) => setSiteConditions(e.target.value)}
                 placeholder="Access, safety, hazards, terrain"
                 maxLength={500} data-testid="daily-log-site" className={INPUT_CLS} />
        </FieldWrap>
      </div>

      {/* 4. Tasks completed */}
      <div className="mt-4">
        <FieldWrap label="Tasks completed">
          <div className="flex gap-2">
            <input
              type="text" value={tasksInput}
              onChange={(e) => setTasksInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
              placeholder="e.g. Mounted 16 panels on roof"
              data-testid="daily-log-task-input" className={INPUT_CLS + " flex-1"} />
            <button type="button" onClick={addTask} data-testid="daily-log-task-add"
                    className="px-3 py-2 rounded-md bg-[#0A2540] text-white text-xs hover:bg-[#123259]">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </FieldWrap>
        {tasks.length > 0 && (
          <ul className="mt-2 space-y-1.5" data-testid="daily-log-tasks">
            {tasks.map((t, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-[#0A2540] dark:text-white">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span className="flex-1">{t}</span>
                <button type="button" onClick={() => setTasks((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-[#94A3B8] hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 5. Materials — used / returned / damaged */}
      <MaterialTable
        title="Materials used"
        rows={used} setRows={setUsed}
        testidPrefix="daily-log-used"
      />
      <MaterialTable
        title="Materials returned"
        rows={returned} setRows={setReturned}
        testidPrefix="daily-log-returned"
      />
      <MaterialTable
        title="Damaged materials"
        rows={damaged} setRows={setDamaged}
        withReason
        testidPrefix="daily-log-damaged"
      />

      {/* 6. Issues */}
      <FieldWrap label="Issues encountered" className="mt-4">
        <textarea rows="2" value={issues} onChange={(e) => setIssues(e.target.value)}
                  placeholder="Anything that blocked or delayed the work"
                  maxLength={2000} data-testid="daily-log-issues" className={INPUT_CLS} />
      </FieldWrap>

      {/* 7. Customer notes (visible to client) */}
      <div className="mt-4 border-t border-[#E2E8F0] dark:border-slate-700 pt-4">
        <div className="flex items-center gap-2 mb-1">
          <Eye className="w-3.5 h-3.5 text-emerald-600" />
          <span className="text-[11px] uppercase tracking-wider text-emerald-600 font-bold">Customer-visible note</span>
        </div>
        <p className="text-[11px] text-[#475569] dark:text-slate-400 mb-1.5">
          This message appears on the client's project timeline.
        </p>
        <textarea rows="2" value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder="e.g. Panels are up! Wiring tomorrow morning."
                  maxLength={2000} data-testid="daily-log-customer-notes"
                  className={INPUT_CLS + " border-emerald-200 dark:border-emerald-800 focus:border-emerald-500 focus:ring-emerald-200"} />
      </div>

      {/* 8. Internal notes (never visible to client) */}
      <div className="mt-4 border border-amber-200 dark:border-amber-700/50 bg-amber-50/60 dark:bg-amber-900/10 rounded-md p-3">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-[11px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-bold">Internal note</span>
        </div>
        <p className="text-[11px] text-amber-800 dark:text-amber-300 mb-1.5">
          Only PowerBuilt staff see this. Never sent to the customer.
        </p>
        <textarea rows="2" value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="e.g. Homeowner wants extra outlet — quote separately."
                  maxLength={2000} data-testid="daily-log-internal-notes"
                  className={INPUT_CLS + " border-amber-300 dark:border-amber-700 bg-white/60 dark:bg-slate-900/60"} />
      </div>

      {/* 9. Follow-up */}
      <label className="flex items-center gap-2 mt-4 cursor-pointer">
        <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)}
               data-testid="daily-log-followup" className="accent-[#C9A356]" />
        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-sm text-[#0A2540] dark:text-white">Follow-up required</span>
      </label>
      {followUp && (
        <textarea rows="2" value={followUpNotes} onChange={(e) => setFollowUpNotes(e.target.value)}
                  placeholder="What needs to happen next?"
                  maxLength={500} data-testid="daily-log-followup-notes"
                  className={INPUT_CLS + " mt-2"} />
      )}

      {/* 10. Customer signature */}
      <div className="mt-4">
        {!signatureOpen && !signatureData && (
          <button type="button" onClick={() => setSignatureOpen(true)}
                  data-testid="daily-log-sign-open"
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-[#0A2540] text-[#0A2540] dark:text-white dark:border-slate-500 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800">
            <PenTool className="w-3.5 h-3.5" /> Capture customer signature
          </button>
        )}
        {signatureData && (
          <div className="flex items-center gap-3">
            <img src={signatureData} alt="Signature" className="h-16 rounded border border-[#E2E8F0]" />
            <button type="button" onClick={() => { setSignatureData(""); setSignatureOpen(true); }}
                    className="text-xs text-[#C9A356] hover:underline">Redo</button>
          </div>
        )}
        {signatureOpen && (
          <SignaturePad
            onClose={() => setSignatureOpen(false)}
            onSave={(data) => { setSignatureData(data); setSignatureOpen(false); }}
          />
        )}
      </div>

      {/* Photos */}
      {photos.length > 0 && (
        <div className="mt-4 flex gap-2 flex-wrap">
          {photos.map((url, i) => (
            <div key={url} className="relative">
              <img src={url} alt="" className="h-16 w-16 object-cover rounded-md border border-[#E2E8F0]" />
              <button
                type="button"
                onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center shadow"
                aria-label="Remove photo"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-5 pt-4 border-t border-[#E2E8F0] dark:border-slate-700">
        <label className="inline-flex items-center gap-1.5 text-xs text-[#475569] dark:text-slate-400 cursor-pointer hover:text-[#0A2540] dark:hover:text-white">
          <ImageIcon className="w-3.5 h-3.5" />
          {uploading ? "Uploading…" : `Add photo (${photos.length}/8)`}
          <input
            type="file" accept="image/*" multiple hidden
            data-testid="daily-log-photo-input"
            onChange={onPick}
            disabled={uploading || photos.length >= 8}
          />
        </label>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !summary.trim()}
          data-testid="daily-log-submit"
          className="btn-accent text-sm disabled:opacity-50"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" /> {submitting ? "Posting…" : "Submit report"}
        </button>
      </div>
    </div>
  );
}

// ---------- helpers ----------

const INPUT_CLS = "w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356]";

function FieldWrap({ label, hint, className = "", children }) {
  return (
    <div className={className}>
      <label className="text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-medium mb-1 block">
        {label}
      </label>
      {children}
      {hint && <div className="text-[10px] text-[#94A3B8] mt-1">{hint}</div>}
    </div>
  );
}

function MaterialTable({ title, rows, setRows, withReason, testidPrefix }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("ea");
  const [reason, setReason] = useState("");
  const add = () => {
    const n = name.trim();
    if (!n) return;
    const row = { name: n, qty: Number(qty) || 0, unit: unit || "ea" };
    if (withReason) row.reason = reason.trim();
    setRows((prev) => [...prev, row].slice(0, 40));
    setName(""); setQty(""); setUnit("ea"); setReason("");
  };
  return (
    <div className="mt-4">
      <FieldWrap label={title}>
        <div className="grid grid-cols-12 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Item name"
                 data-testid={`${testidPrefix}-name`}
                 className={INPUT_CLS + ` ${withReason ? "col-span-4" : "col-span-6"}`} />
          <input value={qty} onChange={(e) => setQty(e.target.value)} type="number" step="0.01" min="0" placeholder="Qty"
                 data-testid={`${testidPrefix}-qty`} className={INPUT_CLS + " col-span-2"} />
          <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="unit"
                 data-testid={`${testidPrefix}-unit`} className={INPUT_CLS + " col-span-2"} />
          {withReason && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason"
                   data-testid={`${testidPrefix}-reason`} className={INPUT_CLS + " col-span-3"} />
          )}
          <button type="button" onClick={add}
                  data-testid={`${testidPrefix}-add`}
                  className="col-span-1 rounded-md bg-[#0A2540] text-white text-xs hover:bg-[#123259] flex items-center justify-center">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </FieldWrap>
      {rows.length > 0 && (
        <ul className="mt-2 text-sm space-y-1">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2 text-[#0A2540] dark:text-white">
              <span className="tabular-nums font-medium w-14 text-right">{r.qty} {r.unit}</span>
              <span className="flex-1 truncate">{r.name}{r.reason ? ` — ${r.reason}` : ""}</span>
              <button type="button" onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-[#94A3B8] hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SignaturePad({ onClose, onSave }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };
  const start = (e) => {
    drawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => { drawingRef.current = false; };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0A2540";
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  };
  const save = () => onSave(canvasRef.current.toDataURL("image/png"));

  return (
    <div className="mt-3 p-3 border border-[#E2E8F0] dark:border-slate-700 rounded-md">
      <div className="text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-medium mb-2">
        Customer signs below to acknowledge today's work
      </div>
      <canvas
        ref={canvasRef}
        width={600} height={160}
        className="w-full border border-dashed border-[#C9A356] rounded bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-pad"
      />
      <div className="flex items-center gap-2 mt-2">
        <button type="button" onClick={clear} className="text-xs text-[#475569] hover:underline">Clear</button>
        <button type="button" onClick={onClose} className="text-xs text-[#475569] hover:underline ml-auto">Cancel</button>
        <button type="button" onClick={save} data-testid="signature-save"
                className="btn-accent text-xs px-3 py-1.5">Save signature</button>
      </div>
    </div>
  );
}
